import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Tag, Trash2 } from 'lucide-react';

const TagManagerModal = ({ tags, onClose, onCreateTag, onDeleteTag }) => {
  const { t } = useTranslation();
  const [newTagName, setNewTagName] = useState('');
  const [newTagColor, setNewTagColor] = useState('#2563eb');

  const handleCreate = () => {
    if (!newTagName.trim()) {
      alert(t('media.enterTagName'));
      return;
    }

    onCreateTag(newTagName, newTagColor);
    setNewTagName('');
    setNewTagColor('#2563eb');
  };

  const predefinedColors = [
    '#2563eb', // Blue
    '#10b981', // Green
    '#f59e0b', // Amber
    '#ef4444', // Red
    '#8b5cf6', // Purple
    '#ec4899', // Pink
    '#06b6d4', // Cyan
    '#f97316', // Orange
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {t('media.manageTags')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        {/* Create New Tag */}
        <div className="mb-6">
          <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
            {t('media.createNewTag')}
          </h4>
          <div className="flex gap-2">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              placeholder={t('media.tagName')}
              className="flex-1 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleCreate();
                }
              }}
            />
            <input
              type="color"
              value={newTagColor}
              onChange={(e) => setNewTagColor(e.target.value)}
              className="w-12 h-10 border border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer"
            />
            <button
              onClick={handleCreate}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
            >
              {t('common.create')}
            </button>
          </div>

          {/* Quick Color Picker */}
          <div className="flex gap-2 mt-2">
            {predefinedColors.map(color => (
              <button
                key={color}
                onClick={() => setNewTagColor(color)}
                className={`w-6 h-6 rounded border-2 transition-all ${
                  newTagColor === color 
                    ? 'border-gray-900 dark:border-white scale-110' 
                    : 'border-gray-300 dark:border-gray-600'
                }`}
                style={{ backgroundColor: color }}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Existing Tags */}
        <div>
          <h4 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
            {t('media.existingTags')} ({tags.length})
          </h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {tags.length === 0 ? (
              <p className="text-gray-500 dark:text-gray-400 text-center py-4 text-sm">
                {t('media.noTagsYet')}
              </p>
            ) : (
              tags.map(tag => (
                <div
                  key={tag.id}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg group"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded"
                      style={{ backgroundColor: tag.color }}
                    ></div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {tag.name}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      ({tag.usage_count || 0} {t('media.uses')})
                    </span>
                  </div>
                  <button
                    onClick={() => onDeleteTag(tag.id)}
                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 dark:hover:bg-red-900 rounded text-red-600 dark:text-red-400 transition-opacity"
                    title={t('common.delete')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Close Button */}
        <div className="mt-6">
          <button
            onClick={onClose}
            className="w-full bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg transition-colors"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TagManagerModal;